import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

export interface SearchableSelectOption {
    value: string;
    label?: string;
}

interface SearchableSelectProps {
    value: string;
    options: SearchableSelectOption[];
    onChange: (value: string) => void;
    placeholder?: string;
    ariaLabel?: string;
    className?: string;
    disabled?: boolean;
    clearOnSelect?: boolean;
    allowEmpty?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
    value,
    options,
    onChange,
    placeholder,
    ariaLabel,
    className,
    disabled = false,
    clearOnSelect = false,
    allowEmpty = false,
}) => {
    const listboxId = useId();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const ignoreNextFocusOpenRef = useRef(false);
    const getLabelForValue = useMemo(() => {
        const optionMap = new Map(options.map((option) => [option.value, option.label ?? option.value]));
        return (nextValue: string) => optionMap.get(nextValue) ?? nextValue;
    }, [options]);
    const [inputValue, setInputValue] = useState(() => (clearOnSelect ? '' : getLabelForValue(value)));
    const [isOpen, setIsOpen] = useState(false);
    const [hasTypedSinceOpen, setHasTypedSinceOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    useEffect(() => {
        setInputValue(clearOnSelect ? '' : getLabelForValue(value));
    }, [clearOnSelect, getLabelForValue, value]);

    const getMatchingOption = (nextInputValue: string) => {
        const normalized = nextInputValue.trim().toLowerCase();
        return options.find((option) => {
            const label = (option.label ?? option.value).toLowerCase();
            return option.value.toLowerCase() === normalized || label === normalized;
        });
    };

    const filterValue = hasTypedSinceOpen ? inputValue.trim().toLowerCase() : '';
    const filteredOptions = options.filter((option) => {
        if (!filterValue) return true;
        const label = (option.label ?? option.value).toLowerCase();
        return label.includes(filterValue) || option.value.toLowerCase().includes(filterValue);
    });
    const selectedOptionIndex = filteredOptions.findIndex((option) => option.value === value);

    useEffect(() => {
        if (!isOpen || filteredOptions.length === 0) {
            setHighlightedIndex(-1);
            return;
        }

        setHighlightedIndex((current) => {
            if (current >= 0 && current < filteredOptions.length) {
                return current;
            }

            return selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
        });
    }, [filteredOptions, isOpen, selectedOptionIndex]);

    const commitInputValue = (nextInputValue: string) => {
        if (allowEmpty && nextInputValue.trim() === '') {
            onChange('');
            return;
        }

        const matchingOption = getMatchingOption(nextInputValue);
        if (matchingOption) {
            onChange(matchingOption.value);
        }
    };

    const resetInputValue = () => {
        if (clearOnSelect) {
            setInputValue('');
            return;
        }

        if (allowEmpty && inputValue.trim() === '') {
            setInputValue('');
            return;
        }

        setInputValue(getLabelForValue(value));
    };

    const openDropdown = () => {
        if (disabled) return;
        setIsOpen(true);
        setHasTypedSinceOpen(false);
        setHighlightedIndex(selectedOptionIndex >= 0 ? selectedOptionIndex : 0);
    };

    const closeDropdown = () => {
        setIsOpen(false);
        setHasTypedSinceOpen(false);
        setHighlightedIndex(-1);
    };

    const handleInputChange = (nextInputValue: string) => {
        setInputValue(nextInputValue);
        setHasTypedSinceOpen(true);
        setIsOpen(true);
        commitInputValue(nextInputValue);
    };

    const handleBlur = () => {
        window.setTimeout(() => {
            const activeElement = document.activeElement;
            if (wrapperRef.current?.contains(activeElement)) {
                return;
            }

            ignoreNextFocusOpenRef.current = false;
            resetInputValue();
            closeDropdown();
        }, 0);
    };

    const handleOptionSelect = (option: SearchableSelectOption) => {
        ignoreNextFocusOpenRef.current = true;
        onChange(option.value);
        if (clearOnSelect) {
            setInputValue('');
        } else {
            setInputValue(option.label ?? option.value);
        }
        closeDropdown();
    };

    return (
        <div ref={wrapperRef} className={`searchable-select ${className ?? ''}`}>
            <input
                type="text"
                role="combobox"
                value={inputValue}
                onChange={(event) => handleInputChange(event.target.value)}
                onFocus={(event) => {
                    if (ignoreNextFocusOpenRef.current) {
                        return;
                    }

                    openDropdown();
                    event.currentTarget.select();
                }}
                onClick={(event) => {
                    openDropdown();
                    event.currentTarget.select();
                }}
                onBlur={handleBlur}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        if (!isOpen) {
                            openDropdown();
                            return;
                        }

                        setHighlightedIndex((current) => {
                            if (filteredOptions.length === 0) return -1;
                            if (current < 0) return 0;
                            return (current + 1) % filteredOptions.length;
                        });
                    }

                    if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        if (!isOpen) {
                            openDropdown();
                            return;
                        }

                        setHighlightedIndex((current) => {
                            if (filteredOptions.length === 0) return -1;
                            if (current < 0) return filteredOptions.length - 1;
                            return (current - 1 + filteredOptions.length) % filteredOptions.length;
                        });
                    }

                    if (event.key === 'Enter' && isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                        event.preventDefault();
                        handleOptionSelect(filteredOptions[highlightedIndex]);
                    }

                    if (event.key === 'Escape') {
                        resetInputValue();
                        closeDropdown();
                    }
                }}
                placeholder={placeholder}
                aria-label={ariaLabel}
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={isOpen && highlightedIndex >= 0 ? `${listboxId}-${highlightedIndex}` : undefined}
                aria-autocomplete="list"
                className="searchable-select-input"
                disabled={disabled}
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
            />
            <button
                type="button"
                className="searchable-select-toggle"
                aria-label={ariaLabel ? `Show ${ariaLabel} options` : 'Show options'}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                    if (isOpen) {
                        resetInputValue();
                        closeDropdown();
                    } else {
                        openDropdown();
                    }
                }}
                disabled={disabled}
                tabIndex={-1}
            >
                ▾
            </button>
            {isOpen && filteredOptions.length > 0 && (
                <div className="searchable-select-menu" role="listbox" id={listboxId}>
                    {filteredOptions.map((option, index) => {
                        const optionLabel = option.label ?? option.value;
                        return (
                            <button
                                key={option.value}
                                id={`${listboxId}-${index}`}
                                type="button"
                                className={`searchable-select-option ${highlightedIndex === index || value === option.value ? 'searchable-select-option-active' : ''}`}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    handleOptionSelect(option);
                                }}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {optionLabel}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
